import { useState, useEffect, type ReactNode } from 'react';
import { TabCount } from "@/components/ui/tab-count";
import { Link2, Copy, Check, Settings, BarChart2, Loader2, Trash2, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import type { Menu } from '@/hooks/useMultiMenu';
import { toast } from 'sonner';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { useAuth } from '@/hooks/useAuth';
import { ShareContactSelector } from '@/components/share/ShareContactSelector';
import { SendItYourselfCard, type SelfShareChannel } from '@/components/share/SendItYourselfCard';
import { useQueryClient } from '@tanstack/react-query';
import { shareTemplates } from '@/lib/menuShareReady';
import { menuSharePitch, type PitchListing } from '@/lib/menuSharePitch';
import { useCuratedMenuMessages } from '@/hooks/useCuratedMenuMessages';
import { Sparkles } from 'lucide-react';
import { AIThinkingLabel } from '@/components/ai/AIThinkingLabel';

export const FEATURE_META: FeatureMeta = {
  id: 'menu-share-link',
  label: 'Share Menu',
  description: 'Send a menu link quickly, then review its activity and access settings separately.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller'],
};
interface MenuShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menu: Menu;
  companyId: string;
}
interface ShareToken {
  id: string;
  token: string;
  label: string | null;
  is_active: boolean;
  use_count: number;
  expires_at: string | null;
  max_uses: number | null;
  contact_user_id: string | null;
  access_mode: 'anyone_with_link' | 'restricted';
}
interface Redemption {
  id: string;
  email: string | null;
  outcome: string;
  redeemed_at: string;
  company_id: string | null;
}
const OUTCOME_LABELS: Record<string, {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
}> = {
  direct_access: {
    label: 'Existing Access',
    variant: 'secondary'
  },
  auto_granted: {
    label: 'Granted',
    variant: 'default'
  },
  request_submitted: {
    label: 'Pending',
    variant: 'outline'
  }
};
const BASE_URL = 'https://cannafax.ai';
function ShareTabContent({ value, active, children }: { value: string; active: boolean; children: ReactNode }) {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  useEffect(() => {
    if (!active || !viewport || !content) return;
    viewport.scrollTop = 0;
    const measure = () => setHasMoreBelow(viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 2);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(content);
    viewport.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      viewport.removeEventListener('scroll', measure);
    };
  }, [active, viewport, content]);

  return (
    <TabsContent value={value} className="relative min-h-0 flex-1 overflow-hidden">
      <div ref={setViewport} tabIndex={0} aria-label={value === 'send' ? 'Send menu link' : 'Menu link activity and settings'} className="overlay-scroll scroll-affordance h-full !mx-0 !px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
        <div ref={setContent} className="space-y-5 py-3">{children}</div>
      </div>
      {hasMoreBelow && (
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 right-3 flex h-8 items-end justify-center bg-gradient-to-t from-background to-transparent pb-1">
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
    </TabsContent>
  );
}
const MenuShareLinkDialog = ({
  open,
  onOpenChange,
  menu,
  companyId
}: MenuShareLinkDialogProps) => {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const [shareToken, setShareToken] = useState<ShareToken | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [label, setLabel] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('send');
  const [templateId, setTemplateId] = useState('fresh');
  const [pitchListings, setPitchListings] = useState<PitchListing[]>([]);
  useEffect(() => {
    if (!open) return;
    let live = true;
    // Own menu's live listings only; no supplier fields are selected.
    supabase.from('menu_listings')
      .select('listing:listings!inner(strain_name,product_form,quantity_grams,price_per_gram,created_at,is_archived)')
      .eq('menu_id', menu.id).limit(200)
      .then(({ data }) => {
        if (!live || !data) return;
        setPitchListings((data as unknown as { listing: PitchListing & { is_archived: boolean | null } }[])
          .map(r => r.listing).filter(x => x && !x.is_archived));
      });
    return () => { live = false; };
  }, [open, menu.id]);
  const baseTemplates = pitchListings.length ? menuSharePitch(menu.name, pitchListings) : shareTemplates(menu.name, 0);
  // One message: the AI draft leads; written pitches are only the fallback when AI has nothing.
  const curated = useCuratedMenuMessages(menu.id, 'share', open);
  const queryClient = useQueryClient();
  // Every self-send (copy, share sheet, text, WhatsApp, email) is a real share:
  // record it server-side so Desk Setup's share step counts it, then refresh Desk.
  const recordSelfShare = async (channel: SelfShareChannel) => {
    const { error } = await supabase.rpc('menu_record_self_share' as never, { p_menu_id: menu.id, p_channel: channel } as never);
    if (error) { console.error('menu_record_self_share failed', error); return; }
    queryClient.invalidateQueries({ queryKey: ['desk-habit'] });
  };
  const aiOptions = curated.data?.options ?? [];
  const templates = aiOptions.length ? aiOptions : baseTemplates;
  const template = templates.find(t => t.id === templateId) ?? templates[0];
  const [draft, setDraft] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState('');
  // Load the chosen angle into the editor once; keep the user's edits until they pick another angle.
  useEffect(() => {
    if (!template || curated.isFetching) return;
    const key = `${template.id}:${template.text}`;
    if (key !== draftKey) { setDraftKey(key); setDraft(template.text); }
  }, [template?.id, template?.text, curated.isFetching]);
  useEffect(() => {
    if (open) {
      setActiveTab('send');
      setTemplateId('');
      setDraft(null);
      setDraftKey('');
      loadTokenData();
    }
  }, [open, menu.id]);
  const loadTokenData = async () => {
    setLoading(true);
    setShortUrl(null);
    try {
      // Get or create the active share token for this menu
      const {
        data: existing, error: tokenError
      } = await supabase.from('menu_share_tokens' as any).select('*').eq('company_id', companyId).eq('menu_id', menu.id).eq('is_active', true).order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      if (tokenError) throw tokenError;
      let token = existing as unknown as ShareToken | null;
      if (!token) {
        // Create a new token
        const {
          data: newToken,
          error
        } = await supabase.from('menu_share_tokens' as any).insert({
          company_id: companyId,
          menu_id: menu.id
        }).select('*').single();
        if (error) throw error;
        token = newToken as unknown as ShareToken;
      }
      // Never show a broken ".../menu-invite/undefined" link: explicit error instead.
      if (!token?.token) throw new Error('Menu link token missing');
      setShareToken(token);
      setLabel(token.label || '');

      // Route through the branded preview host (link.cannafax.ai) so SMS /
      // iMessage / Slack unfurl a rich card. Non-fatal: fall back to the long
      // URL if `shorten-url` errors.
      const longUrl = `${BASE_URL}/menu-invite/${token.token}`;
      try {
        const { data: shortData, error: shortError } = await supabase.functions.invoke(
          'shorten-url',
          { body: { url: longUrl } },
        );
        if (shortError) throw shortError;
        const result = shortData as { shortUrl?: string } | null;
        if (result?.shortUrl) setShortUrl(result.shortUrl);
      } catch (shortErr) {
        console.error('shorten-url failed for menu-invite:', shortErr);
      }

      // Single RPC call: join, enrich, and deduplicate server-side
      const {
        data: activityData,
        error: activityError
      } = await supabase.rpc('get_share_link_activity', {
        p_token_id: token.id,
        p_menu_id: menu.id
      });
      if (activityError) throw activityError;
      const activity = activityData as unknown as {
        redemptions: Redemption[];
        granted_count: number;
        pending_count: number;
      };
      setRedemptions(activity?.redemptions || []);
    } catch (error) {
      console.error('Error loading share token:', error);
      toast.error('Failed to load your menu link');
    } finally {
      setLoading(false);
    }
  };
  const handleCopy = async () => {
    if (!shareToken) return;
    const url = shortUrl || `${BASE_URL}/menu-invite/${shareToken.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Menu link copied', { description: 'Paste it to a buyer. You will see who opens it on Your Desk.' });
    } catch {
      toast.error('Could not copy the link', { description: 'Select the link and copy it manually.' });
    }
    // Note: `menu_shared` is emitted by the `menu_share_tokens_emit_shared` AFTER INSERT
    // trigger (C-AN-06). No client-side trackActivity call here — would duplicate.
  };
  const handleSaveLabel = async () => {
    if (!shareToken) return;
    setSaving(true);
    try {
      const {
        error
      } = await supabase.from('menu_share_tokens' as any).update({
        label: label.trim() || null
      }).eq('id', shareToken.id);
      if (error) throw error;
      setShareToken(prev => prev ? {
        ...prev,
        label: label.trim() || null
      } : null);
      toast.success('Label saved');
    } catch {
      toast.error('Failed to save label');
    } finally {
      setSaving(false);
    }
  };
  const handleAccessModeChange = async (mode: 'anyone_with_link' | 'restricted') => {
    if (!shareToken || shareToken.access_mode === mode) return;
    const prev = shareToken.access_mode;
    setShareToken({ ...shareToken, access_mode: mode });
    try {
      const { error } = await supabase
        .from('menu_share_tokens' as any)
        .update({ access_mode: mode })
        .eq('id', shareToken.id);
      if (error) throw error;
      toast.success(mode === 'restricted'
        ? 'Restricted — new visitors must ask for access'
        : 'Anyone with the link can open listings now');
    } catch {
      setShareToken((t) => (t ? { ...t, access_mode: prev } : t));
      toast.error('Failed to update link access mode');
    }
  };
  const handleDeactivate = async () => {
    if (!shareToken) return;
    try {
      const {
        error
      } = await supabase.from('menu_share_tokens' as any).update({
        is_active: false
      }).eq('id', shareToken.id);
      if (error) throw error;
      toast.success('Menu link deactivated. Existing access is not affected.');
      onOpenChange(false);
    } catch {
      toast.error('Failed to deactivate link');
    }
  };
  const handleContactChange = async (userId: string) => {
    if (!shareToken) return;
    const prev = shareToken.contact_user_id;
    setShareToken({ ...shareToken, contact_user_id: userId });
    setSavingContact(true);
    try {
      const { error } = await supabase.rpc('update_menu_share_token_contact', {
        p_token_id: shareToken.id,
        p_contact_user_id: userId,
      } as never);
      if (error) throw error;
      toast.success('Contact updated');
    } catch (e: any) {
      setShareToken((t) => (t ? { ...t, contact_user_id: prev } : t));
      toast.error('Failed to update contact', { description: e?.message });
    } finally {
      setSavingContact(false);
    }
  };
  const pendingCount = redemptions.filter(r => r.outcome === 'request_submitted').length;
  const grantedCount = redemptions.filter(r => r.outcome === 'auto_granted').length;
  const shareUrl = shortUrl || (shareToken ? `${BASE_URL}/menu-invite/${shareToken.token}` : '');
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg h-[min(39rem,90dvh)] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex min-w-0 items-center gap-2 pr-8">
            <Link2 className="w-4 h-4 shrink-0" />
            <span className="truncate">Share &ldquo;{menu.name}&rdquo;</span>
          </DialogTitle>
        </DialogHeader>

        {loading || curated.isFetching ? <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-10">
            <AIThinkingLabel size="lg" context="description" title="Writing your message…" />
          </div> : <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="shrink-0">
              <TabsTrigger value="send">Send</TabsTrigger>
              <TabsTrigger value="manage">
                Activity &amp; Settings
                {pendingCount > 0 && <TabCount tone="attention" value={pendingCount} />}
              </TabsTrigger>
            </TabsList>

            <ShareTabContent value="send" active={activeTab === 'send'}>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Menu link</Label>
                <div className="flex gap-2">
                  <Input value={shareUrl} readOnly className="font-mono text-base md:text-sm bg-muted" />
                  <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0" aria-label="Copy menu link">
                    {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {shareToken?.access_mode === 'anyone_with_link'
                    ? 'Anyone with this link can open the menu.'
                    : 'New visitors ask for access. Existing partners open it immediately.'}{' '}
                  <button type="button" onClick={() => setActiveTab('manage')} className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
                    Change who can open
                  </button>
                </p>
              </div>

              {shareUrl && (
                <div className="space-y-3">
                {(<>
                {templates.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Message angle">
                    {aiOptions.length > 0 && <Sparkles className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />}
                    {templates.map(t => <Button key={t.id} size="sm" variant={t.id === template?.id ? 'secondary' : 'ghost'} aria-pressed={t.id === template?.id} onClick={() => setTemplateId(t.id)}>
                      {t.label}
                    </Button>)}
                  </div>
                )}
                <SendItYourselfCard
                  url={shareUrl}
                  subject={`${menu.name} — current availability`}
                  message={draft ?? template?.text ?? ''}
                  originalMessage={template?.text}
                  onMessageChange={setDraft}
                  title="Your message"
                  helperText=""
                  rows={6}
                  onDispatch={recordSelfShare}
                />
                </>)}
                </div>
              )}

            </ShareTabContent>

            <ShareTabContent value="manage" active={activeTab === 'manage'}>
              {shareToken && <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Who can open listings</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Button variant={shareToken.access_mode === 'restricted' ? 'default' : 'outline'} size="sm" onClick={() => handleAccessModeChange('restricted')}>Restricted</Button>
                  <Button variant={shareToken.access_mode === 'anyone_with_link' ? 'default' : 'outline'} size="sm" onClick={() => handleAccessModeChange('anyone_with_link')}>Anyone with the link</Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {shareToken.access_mode === 'restricted'
                    ? 'New people request access. Existing partners continue straight to the menu.'
                    : 'Anyone holding this link can open the menu immediately.'}
                </p>
              </div>}

              <ShareContactSelector companyId={companyId} currentUserId={user?.id} value={shareToken?.contact_user_id ?? null} onChange={handleContactChange} disabled={savingContact} helperText="Choose the teammate buyers should contact from the invitation page." />

              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><Settings className="w-3.5 h-3.5" /> Campaign label (optional)</Label>
                <div className="flex gap-2">
                  <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Trade show, buyer group…" maxLength={80} />
                  <Button variant="outline" size="sm" onClick={handleSaveLabel} disabled={saving} className="shrink-0">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}</Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><BarChart2 className="w-3.5 h-3.5" /> Activity</Label>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                  <span>{shareToken?.use_count ?? 0} click{(shareToken?.use_count ?? 0) !== 1 ? 's' : ''}</span>
                  <span>{grantedCount} granted</span>
                  {pendingCount > 0 && <span className="text-warning font-medium">{pendingCount} pending approval</span>}
                </div>
                {redemptions.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : <div className="space-y-1.5">
                  {redemptions.map(r => {
                    const outcome = OUTCOME_LABELS[r.outcome] || { label: 'Recorded', variant: 'secondary' as const };
                    return <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/50 last:border-0">
                      <span className="text-sm truncate text-foreground/80">{r.email || 'Buyer company'}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={outcome.variant} className="text-xs px-1.5 py-0">{outcome.label}</Badge>
                        <span className="text-xs text-muted-foreground">{new Date(r.redeemed_at).toLocaleDateString()}</span>
                      </div>
                    </div>;
                  })}
                </div>}
              </div>

              <div className="pt-2 border-t border-border">
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full" onClick={handleDeactivate}>
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Deactivate link
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-1.5">Companies already approved keep their access.</p>
              </div>
            </ShareTabContent>
          </Tabs>}
      </DialogContent>
    </Dialog>;
};
export default MenuShareLinkDialog;